import { Col, Form, Input, Row, Slider } from 'antd'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { ComponentProps } from './types'
import {
  COEFFICIENT_MAX,
  COEFFICIENT_MIN,
  validateCoefficientRange
} from './utils'

const COEFFICIENT_FIELDS = [
  'coefficient_min',
  'coefficient_default',
  'coefficient_max'
] as const

const DEFAULT_COEFFICIENTS: Record<
  (typeof COEFFICIENT_FIELDS)[number],
  string
> = {
  coefficient_min: COEFFICIENT_MIN.toFixed(2),
  coefficient_default: '5.00',
  coefficient_max: COEFFICIENT_MAX.toFixed(2)
}

export const FinancialProductConfig = ({ field }: ComponentProps) => {
  const { t } = useTranslation()
  const form = Form.useFormInstance()

  const getPath = (key: string) => ['products', field.name, 'config', key]

  useEffect(() => {
    const hasValue = COEFFICIENT_FIELDS.some(
      (key) => form.getFieldValue(getPath(key)) !== undefined
    )

    if (!hasValue) {
      form.setFields(
        COEFFICIENT_FIELDS.map((key) => ({
          name: getPath(key),
          value: DEFAULT_COEFFICIENTS[key]
        }))
      )
    }
  }, [field.name])

  const validateOrder = (_: unknown, value: string) => {
    if (value === '' || value === undefined || value === null) {
      return Promise.resolve()
    }

    const [min, defaultValue, max] = COEFFICIENT_FIELDS.map((key) =>
      Number(form.getFieldValue(getPath(key)))
    )

    if ([min, defaultValue, max].some((num) => Number.isNaN(num))) {
      return Promise.resolve()
    }

    if (min > max || defaultValue < min || defaultValue > max) {
      return Promise.reject(new Error(t('coefficient_order_error')))
    }

    return Promise.resolve()
  }

  const handleSliderChange = (values: number[]) => {
    const paths = COEFFICIENT_FIELDS.map((key) => getPath(key))

    form.setFields(
      values.map((value, index) => ({
        name: paths[index],
        value: value.toFixed(2)
      }))
    )
    form.validateFields(paths)
  }

  return (
    <>
      <Form.Item noStyle shouldUpdate>
        {() => {
          const sliderValue = COEFFICIENT_FIELDS.map((key) => {
            const value = Number(form.getFieldValue(getPath(key)))

            return Number.isNaN(value) ? COEFFICIENT_MIN : value
          })

          return (
            <Form.Item label={t('coefficients')} className="m-0">
              <Slider
                range={{ draggableTrack: true }}
                min={COEFFICIENT_MIN}
                max={COEFFICIENT_MAX}
                step={0.01}
                value={sliderValue}
                onChange={handleSliderChange}
              />
            </Form.Item>
          )
        }}
      </Form.Item>

      <Row gutter={16}>
        {COEFFICIENT_FIELDS.map((key) => (
          <Col span={8} key={key}>
            <Form.Item
              {...field}
              name={[field.name, 'config', key]}
              label={t(key)}
              dependencies={COEFFICIENT_FIELDS.filter(
                (dependency) => dependency !== key
              ).map((dependency) => [field.name, 'config', dependency])}
              rules={[
                { required: true },
                {
                  validator: (_, value) => validateCoefficientRange(value, t)
                },
                ...(key === 'coefficient_default'
                  ? [{ validator: validateOrder }]
                  : [])
              ]}
            >
              <Input
                type="number"
                placeholder={t(`${key}_placeholder`)}
                min={COEFFICIENT_MIN}
                max={COEFFICIENT_MAX}
                step={0.01}
              />
            </Form.Item>
          </Col>
        ))}
      </Row>
    </>
  )
}
